import { Text, View } from "react-native";
import { useResidentContext } from "../../provider/ResidentProvider";
import { convertDateToDDMMYYYY } from "ui/utils/dateUtils";
import { useMemo, useRef } from "react";
import KeyValueGrid from "ui/organisms/KeyValueGrid";
import {
  ResidentStatusEnum,
  StatusIndicatorMapping,
  StatusTitleMapping,
} from "app/services/api/types/residents/enums/ResidentStatusEnum";
import { EditResidentAdmissionModal } from "../modals/EditResidentAdmissionModal";
import { EditButton } from "../../molecules/EditButton";
import { ViewDocument } from "app/components/atoms/ViewDocument";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { formatEnumText } from "ui/utils/nameUtil";
import { FormEnums } from "../../../../services/api/types/forms/enums/FormEnums";
import { useFormHistoryWithOffline } from "../../../offline/hooks/useFormHistoryWithOffline";
import ResidentAdmissionChecklistForm from "../../organisms/ResidentAdmissionChecklistForm";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";

export default function ResidentAdmissionTemplate() {
  const { resident } = useResidentContext();
  const ref =
    useRef<React.ComponentRef<typeof EditResidentAdmissionModal>>(null);
  const startEdit = () => {
    ref.current?.show(resident);
  };

  const renderResidentStatus = (
    status?: ResidentStatusEnum,
    statusReason?: string,
  ) => {
    if (!status) return "N/A";

    return (
      <View>
        <StatusIndicator
          status={StatusIndicatorMapping[status]}
          statusTitle={StatusTitleMapping[status]}
        />
        {statusReason ? (
          <>
            <Text className="text-primary-400 font-medium mt-5 mb-1">
              Reason
            </Text>
            <Text>{statusReason}</Text>
          </>
        ) : null}
      </View>
    );
  };

  const admissionDetails = useMemo(
    () => [
      {
        label: "Check in",
        value: resident?.admission_start_date
          ? convertDateToDDMMYYYY(resident.admission_start_date)
          : "N/A",
      },
      {
        label: "Check out",
        value: resident?.admission_end_date
          ? convertDateToDDMMYYYY(resident.admission_end_date)
          : "N/A",
      },
      {
        label: "Attending for",
        value:
          formatEnumText(
            resident?.attending_for ? resident?.attending_for : "",
          ) || "N/A",
      },
      {
        label: "Home allocation",
        value: resident?.location?.name || "N/A",
      },
      {
        label: "Bed",
        value: resident?.bed?.bed_number || "N/A",
      },
      {
        label: "Admission Type",
        value: resident?.admission_type || "N/A",
      },
      {
        label: "Resident Status",
        value: renderResidentStatus(resident?.status, resident?.status_reason),
      },
      {
        label: "Dependency",
        value: resident?.dependency || "N/A",
      },
    ],
    [resident],
  );

  const tag = FormEnums.ADMISSION_CHECKLIST;

  const { data, isLoading } = useFormHistoryWithOffline({
    tag,
    residentId: resident?.id,
  });

  return (
    <View className="resident-container">
      <EditButton
        startEdit={startEdit}
        permission={PermissionsEnums.UPDATE_RESIDENT}
      />

      {resident?.admission_start_date ? (
        <>
          <KeyValueGrid items={admissionDetails} />
        </>
      ) : (
        <Text className="text-typography-600">
          No admission details listed.
        </Text>
      )}
      <View className="border border-gray-200 w-full my-12" />
      <PermissionCheck permission={PermissionsEnums.VIEW_FORM_ENTRIES}>
        <ResidentAdmissionChecklistForm />
        <View className="border border-gray-200 w-full my-12" />
      </PermissionCheck>
      {resident?.other_notes || resident?.other_notes_media ? (
        <View className="mb-8">
          <Text className="font-semibold mb-4">Other Notes</Text>
          {resident.other_notes ? <Text>{resident.other_notes}</Text> : null}
          {resident.other_notes_media ? (
            <View className="w-[286px] h-[260px] aspect-square border border-gray-200  rounded-xl overflow-hidden mt-8">
              <ViewDocument document={resident.other_notes_media} />
            </View>
          ) : null}
        </View>
      ) : (
        <Text className="text-typography-600">No notes listed.</Text>
      )}

      <EditResidentAdmissionModal ref={ref} />
    </View>
  );
}
